import { Link } from "@tanstack/react-router";
import type { RouteTrack, StepView } from "@/app/queries";
import { Button } from "@/ui/Button";
import { ProgressRing } from "@/ui/Progress";
import { continueLabel, gateLead, nextAction, stepBars } from "./derive";

/** Секция «сейчас» (S1 · 1a): активный шаг, кольцо гейтов трека, три полосы. */
export function NowSection({ current, track }: { current: StepView; track: RouteTrack }) {
  const action = nextAction(current);
  const bars = stepBars(current);
  return (
    <section className="overflow-hidden rounded-lg border border-bd2 bg-card" aria-labelledby="now-title">
      <div className="flex items-center gap-2.5 border-b border-bd bg-bg2 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[.06em] text-mut">
        <span aria-hidden className="size-1.5 rounded-full bg-acc" />
        сейчас
      </div>
      <div className="grid gap-6 p-5 md:grid-cols-[minmax(0,1fr)_220px]">
        <div>
          <div className="mb-2 font-mono text-[11.5px] text-accdim">
            {current.track} · шаг {current.step_index} из {current.track_total}
          </div>
          <h2 id="now-title" className="m-0 mb-2.5 font-mono text-[27px] font-bold leading-[1.15] tracking-[-.02em] text-ink">
            {current.title}
          </h2>
          <p className="m-0 mb-[18px] max-w-[560px] text-[14px] text-ink2 text-pretty">{gateLead(current)}</p>
          <div className="flex flex-wrap items-center gap-2.5">
            <Link to="/step/$id" params={{ id: current.id }} className="no-underline">
              <Button variant="primary" tabIndex={-1}>
                {continueLabel(action)}
              </Button>
            </Link>
            <Link to="/step/$id" params={{ id: current.id }} className="no-underline">
              <Button variant="secondary" tabIndex={-1}>
                открыть шаг целиком
              </Button>
            </Link>
          </div>
        </div>
        <div className="flex flex-col items-center gap-2.5 border-t border-bd pt-4 md:border-l md:border-t-0 md:pl-6 md:pt-0">
          <ProgressRing done={track.done} total={track.total} size={96} stroke={7} />
          <span className="text-center font-mono text-[11px] leading-[1.4] text-mut">
            гейтов сдано
            <br />в треке «{track.name}»
          </span>
        </div>
      </div>
      <div className="grid border-t border-bd md:grid-cols-3" data-testid="step-bars">
        {bars.map((b) => {
          const pct = b.total > 0 ? Math.round((100 * b.done) / b.total) : 0;
          return (
            <div key={b.label} className="border-b border-bd px-5 py-3 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0">
              <div className="mb-[7px] flex justify-between font-mono text-[11px] text-mut">
                <span>{b.label}</span>
                <span>
                  {b.done} / {b.total}
                </span>
              </div>
              <div className="h-1 overflow-hidden rounded-[2px] bg-card2" role="progressbar" aria-label={b.label} aria-valuenow={b.done} aria-valuemax={b.total}>
                <div className="h-full bg-acc" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
